import React, { useState, useEffect } from "react";
import { Briefcase, Truck, MapPin, DollarSign, Loader2, CheckCircle2, X } from "lucide-react";
import api from "../lib/api";

const DRIVER_TYPES = [
  "Owner-Operator Hotshot Driver",
  "Cargo Van Driver",
  "RGN & Multi-Axle Driver",
  "Box Truck Driver",
];

const empty = { name: "", email: "", phone: "", driver_type: DRIVER_TYPES[0], equipment: "", experience: "", authority: "", home_base: "", notes: "", job_title: "" };

const Careers = () => {
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState(empty);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { api.get("/jobs").then((r) => setJobs(r.data || [])).catch(() => {}); }, []);

  const openApply = (job) => {
    setForm({ ...empty, job_title: job ? job.title : "", driver_type: job ? job.title.replace("Owner-Operator — ", "Owner-Operator ") : DRIVER_TYPES[0], equipment: job ? job.equipment : "" });
    setShowForm(true);
    setDone(false);
    setTimeout(() => { const el = document.getElementById("careers-form"); if (el) el.scrollIntoView({ behavior: "smooth", block: "center" }); }, 50);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/job-applications", form);
      setDone(true);
      setShowForm(false);
    } catch (err) { alert("Something went wrong. Please try again."); }
    finally { setBusy(false); }
  };

  return (
    <section className="careers" id="careers" data-testid="careers-section">
      <div className="container">
        <div className="section-head">
          <span className="kicker"><Briefcase size={13} style={{ verticalAlign: "-2px" }} /> Driver Careers</span>
          <h2>Drive with Flying Fleet</h2>
          <p>We're sourcing loads for owner-operators and drivers across hotshot, cargo van, RGN &amp; multi-axle, and box truck freight. Apply below and our dispatch team will reach out.</p>
        </div>

        <div className="careers-grid">
          {jobs.map((j) => (
            <div className="career-card" key={j.id} data-testid={`job-card`}>
              <div className="career-icon"><Truck strokeWidth={2} /></div>
              <h3>{j.title}</h3>
              <div className="career-meta">
                {j.equipment && <span><Truck size={13} /> {j.equipment}</span>}
                {j.location && <span><MapPin size={13} /> {j.location}</span>}
                {j.pay && <span><DollarSign size={13} /> {j.pay}</span>}
              </div>
              <p>{j.description}</p>
              <button className="btn btn-primary btn-block" onClick={() => openApply(j)} data-testid="job-apply-btn">Apply now</button>
            </div>
          ))}
        </div>

        {done && (
          <div className="careers-done" data-testid="careers-done">
            <CheckCircle2 size={40} color="#3fae4a" />
            <div><h3>Application received!</h3><p>Thanks — our dispatch team will review and contact you soon. You can also call 815-406-9774.</p></div>
          </div>
        )}

        {showForm && (
          <form className="careers-form" id="careers-form" onSubmit={submit} data-testid="careers-form">
            <div className="careers-form-head">
              <h3>Driver application {form.job_title ? `— ${form.job_title}` : ""}</h3>
              <button type="button" className="careers-close" onClick={() => setShowForm(false)} aria-label="Close"><X size={18} /></button>
            </div>
            <div className="form-row">
              <div className="field"><label>Full Name</label><input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="app-name" /></div>
              <div className="field"><label>Email</label><input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="app-email" /></div>
            </div>
            <div className="form-row">
              <div className="field"><label>Phone</label><input required value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="app-phone" /></div>
              <div className="field"><label>Driver Type</label>
                <select value={form.driver_type} onChange={(e) => set("driver_type", e.target.value)} data-testid="app-driver-type">
                  {DRIVER_TYPES.map((d) => <option key={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="field"><label>Equipment / Trailer</label><input value={form.equipment} onChange={(e) => set("equipment", e.target.value)} placeholder="e.g. 40ft gooseneck, 26ft box" data-testid="app-equipment" /></div>
              <div className="field"><label>Years of Experience</label><input value={form.experience} onChange={(e) => set("experience", e.target.value)} placeholder="e.g. 3 years" /></div>
            </div>
            <div className="form-row">
              <div className="field"><label>Do you have your own authority?</label>
                <select value={form.authority} onChange={(e) => set("authority", e.target.value)}><option value="">Select</option><option>Yes — active MC/DOT</option><option>No — leasing on</option><option>Not sure</option></select>
              </div>
              <div className="field"><label>Home Base (City/State)</label><input value={form.home_base} onChange={(e) => set("home_base", e.target.value)} /></div>
            </div>
            <div className="field"><label>Notes</label><textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Preferred lanes, availability, anything else..."></textarea></div>
            <button className="btn btn-primary btn-block" disabled={busy} data-testid="app-submit">{busy ? <Loader2 size={16} className="spin" /> : null} Submit Application</button>
          </form>
        )}
      </div>
    </section>
  );
};

export default Careers;
